import React from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { PanoramaProvider, usePanorama } from "./context/PanoramaContext"
import { TabsProvider, useTabs }         from "./context/TabsContext"
import Header               from "./components/Header"
import MilestonesSection    from "./components/MilestonesSection"
import SummarySection       from "./components/SummarySection"
import LifecycleStrip       from "./components/LifecycleStrip"
import KpiStrip             from "./components/KpiStrip"
import TabNav               from "./components/TabNav"
import WbsTree              from "./components/WbsTree"
import ProjectGantt         from "./components/ProjectGantt"
import BudgetsSection       from "./components/BudgetsSection"
import AvenantsSection      from "./components/AvenantsSection"
import EngagementsSection   from "./components/EngagementsSection"
import SubcontractsSection  from "./components/SubcontractsSection"
import ExpensesSection      from "./components/ExpensesSection"
import ReservesSection      from "./components/ReservesSection"
import LibrarySection       from "./components/LibrarySection"
import PaymentsSection      from "./components/PaymentsSection"
import InterventionsSection from "./components/InterventionsSection"
import HoursSection         from "./components/HoursSection"
import EquipmentsSection    from "./components/EquipmentsSection"
import DocumentsGrid        from "./components/DocumentsGrid"
import FinancesSection      from "./components/FinancesSection"
import ValorisationSection  from "./components/ValorisationSection"
import MarginsSection       from "./components/MarginsSection"
import AnalyticsCascadeSection from "./components/AnalyticsCascadeSection"
import AnalyticsAlertsBanner    from "./components/AnalyticsAlertsBanner"

const Layout = ({ license }) => {
  const { state, refresh } = usePanorama()
  const { current, goTo } = useTabs()

  const [pendingPurchaseOrderId, setPendingPurchaseOrderId] = React.useState(null)

  React.useEffect(() => {
    const handler = event => {
      setPendingPurchaseOrderId(event.detail?.purchaseOrderId || null)
      goTo("expenses")
    }
    window.addEventListener("project:create-purchase-invoice", handler)
    return () => window.removeEventListener("project:create-purchase-invoice", handler)
  }, [goTo])
  const { project, kpis, tasks, gantt, interventions, components, hours, documents, financials, avenants, engagements, subcontracts, expenses, purchase_invoices, reserves, acceptance, library, payments, retention, valorisation, margins, down_payment, dgd, situations, guarantee, standard_invoicing, bank_guarantee, policies, enabled_cost_categories, analytics_screen, analytic_advanced, analytic_lookups, analytic_alerts, equipments } = state
  const standard = project.invoicing_method === "standard"

  const SECTIONS = {
    summary:       () => <SummarySection      project={project} kpis={kpis} hours={hours} documents={documents} editPath={project.edit_path} policies={policies} />,
    wbs:           () => <WbsTree              tasks={tasks} project={project} license={license} />,
    gantt:         () => <ProjectGantt         tasks={tasks} project={project} gantt={gantt} policies={policies} />,
    reserves:      () => <ReservesSection      reserves={reserves} policies={policies} acceptance={acceptance} />,
    interventions: () => <InterventionsSection interventions={interventions} project={project} />,

    engagements:   () => <EngagementsSection   engagements={engagements} />,
    subcontracts:  () => <SubcontractsSection  subcontracts={subcontracts} policies={policies} />,
    expenses:      () => <ExpensesSection      expenses={expenses} purchaseInvoices={purchase_invoices} policies={policies} enabledCategories={enabled_cost_categories} analyticAdvanced={analytic_advanced} analyticLookups={analytic_lookups} projectDefaults={{ departmentId: project.department?.id, tradeId: project.trade?.id }} pendingPurchaseOrderId={pendingPurchaseOrderId} onPendingHandled={() => setPendingPurchaseOrderId(null)} />,
    library:       () => <LibrarySection       library={library} policies={policies} />,

    milestones:    () => (
      <MilestonesSection
        standard={standard}
        standardInvoicing={standard_invoicing}
        bankGuarantee={bank_guarantee}
        downPayment={down_payment}
        situations={situations}
        acceptance={acceptance}
        retention={retention}
        guarantee={guarantee}
        dgd={dgd}
        project={project}
        policies={policies}
      />
    ),
    avenants:      () => <AvenantsSection      avenants={avenants} policies={policies} />,
    payments:      () => <PaymentsSection      payments={payments} policies={policies} />,
    finances:      () => <FinancesSection      financials={financials} />,

    budgets:       () => <BudgetsSection       components={components} />,
    margins:       () => <MarginsSection       margins={margins} />,
    valorisation:  () => <ValorisationSection  valorisation={valorisation} project={project} policies={policies} />,
    analytics:     () => <AnalyticsCascadeSection analytics={analytics_screen} project={project} />,

    hours:         () => <HoursSection         hours={hours} />,
    equipments:    () => <EquipmentsSection    equipments={equipments} onChange={refresh} />,
    documents:     () => <DocumentsGrid        documents={documents} policies={policies} />,
  }

  const renderSection = SECTIONS[current]

  return (
    <div className="bg-slate-50 min-h-full pb-12">
      <Header project={project} editPath={project.edit_path} />
      <LifecycleStrip
        project={project}
        standard={standard}
        downPayment={down_payment}
        situations={situations}
        acceptance={acceptance}
        dgd={dgd}
        retention={retention}
      />
      <KpiStrip kpis={kpis} hours={hours} />
      <AnalyticsAlertsBanner alerts={analytic_alerts} />
      <TabNav />
      {/* The only place the gap under the tab bar is decided. Each section used
          to bring its own (mt-6 here, mt-8 there, my-4 elsewhere), so the page
          jumped by a few pixels every time you switched tab. */}
      {/* The page keeps one vertical rhythm around the bar: 24 px above it and
          24 px below, the same gap that already separates the lifecycle strip
          from the figures. 15 and not 16 because the bar's own 1 px rule sits
          inside this gap and counts towards it. */}
      <div className="pt-[15px]">{renderSection ? renderSection() : null}</div>
    </div>
  )
}

const App = ({ data, license }) => {
  if (!data) return null
  return (
    <PanoramaProvider data={data}>
      <TabsProvider>
        <Layout license={license} />
      </TabsProvider>
    </PanoramaProvider>
  )
}

export default App
